import React, { useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Countdown from "react-countdown";
import useWindowSize from "../../../hooks/useWindowSize";
import Skeleton from "react-loading-skeleton";
import ThousandsSeparator from "../../../utils/thousands-separator";
import { roundFloat } from "../../../utils/round-float";
import { useAppTranslation } from "../../../i18n/useAppTranslation";
import { getTroopStateLabel } from "./constants";
import { TroopBoxProps } from "./types";
import CalculateTroopAbility from "./CalculateTroopAbility";
import AlteredLabel from "../../AlteredLabel";
import ModifiedStat from "../../ModifiedStat";
import { alteredThemeOf, healthPair } from "../../../utils/statModifiers";

const TinyView: React.FC<TroopBoxProps> = (props) => {
  const ref = useRef<any>();
  const windowSize = useWindowSize();
  const { t } = useAppTranslation(["barracks"]);

  const abilities = useMemo(() => {
    return props?.abilities?.filter((ability) => ability?.Value > 0);
  }, [props?.abilities]);
  // Set while an event modifier with a label changes this troop.
  const alteredTheme = alteredThemeOf(props);

  return (
    <div className="pb-3">
      <div
        ref={ref}
        style={{ paddingBottom: 30 }}
        className={
          "land-box-tiny overflow-hidden pt-3  text-light " + props.className
        }
      >
        {!props.loading && alteredTheme && (
          <AlteredLabel theme={alteredTheme} kind="troop" placement="top" />
        )}
        <div className="lb-body d-flex px-2 ">
          <div
            ref={ref}
            className={"text-center mb-4 " + (!props.loading && "d-none")}
          >
            <span>
              <Skeleton
                className="ms-2"
                width={65}
                height={65}
                borderRadius={15}
                circle={windowSize.width >= 1400 ? true : false}
              />
            </span>
          </div>
          {!props.loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              style={{ position: "relative" }}
              className="image px-2 text-center"
            >
              <div
                style={{
                  width: "100%",
                  background: "#332e27",
                  borderRadius: "50%",
                }}
                className="d-flex p-relative align-items-center p-1 justify-content-center"
              >
                <img
                  style={{
                    borderRadius: "50%",
                    marginBottom: props.nft ? 20 : 0,
                  }}
                  width={50}
                  src={props?.icon || props?.troop?.icon}
                  alt={props?.name || props?.troop?.name}
                  className=""
                />
                {props.leverage && (
                  <div
                    style={{
                      bottom: -10,
                      width: 30,
                      height: 35,
                      right: 0,
                      left: 0,
                      fontSize: 10,
                    }}
                    className="p-absolute mx-auto leverage"
                  >
                    {props.leverage}X
                  </div>
                )}
              </div>
            </motion.div>
          )}
          <div className="lb-title w-100">
            <div className="d-flex w-100  align-items-center">
              <h3 style={{ fontSize: 18 }} className="h5 mb-0">
                {props.loading ? (
                  <Skeleton width={"90px"} borderRadius={10} height={20} />
                ) : (
                  <>
                    {props?.name || props?.troop?.name}
                    {!props.isLearn && !props.sellCard && (
                      <small style={{ fontSize: 11 }}>
                        {" "}
                        (
                        {props.sellCard
                          ? t("barracks:states.clickForDetails")
                          : props.train
                          ? t("barracks:states.available")
                          : getTroopStateLabel(props?.state, t)}
                        )
                      </small>
                    )}
                  </>
                )}
              </h3>
              {props.sellCard && (
                <div
                  style={{ color: "#399C61", fontWeight: 400 }}
                  className="fs-med ms-1"
                >
                  {t("barracks:stats.hp")}: {props.maxHealth}
                </div>
              )}
            </div>
            <div className="d-flex align-items-center">
              {props.loading && (
                <Skeleton
                  width={"90px"}
                  className="ms-2"
                  borderRadius={5}
                  height={15}
                />
              )}
              {props.loading && (
                <Skeleton
                  width={"30px"}
                  className="ms-2"
                  borderRadius={5}
                  height={15}
                />
              )}
              {props.ask || props.bid ? (
                <div
                  style={{ fontWeight: 400 }}
                  className="align-items-center mt-1 justify-content-between w-100 fs-med "
                >
                  <div>
                    {t("barracks:stats.buyersPrice")}: &nbsp;
                    <span className="text-success ">
                      {ThousandsSeparator(props.bid?.toString() || "0") || 0}
                    </span>{" "}
                    {t("barracks:stats.cleg")}
                  </div>
                  <div>
                    {t("barracks:stats.sellerPrice")}: &nbsp;
                    <span className="text-danger ">
                      {ThousandsSeparator(props.ask?.toString() || "0")}
                    </span>{" "}
                    {t("barracks:stats.cleg")}
                  </div>
                </div>
              ) : (
                ""
              )}
              {!props.sellCard && props?.health?.toString() && (
                <div className="fs-tiny mt-2 px-2 text-center rounded bg-gray p-relative overflow-hidden percent-badge">
                  <div
                    style={{
                      width: (props?.health * 100) / props?.maxHealth + "%",
                      position: "absolute",
                      height: "100%",
                      left: 0,
                      backgroundColor: "var(--theme-color-green-dark)",
                      top: 0,
                    }}
                  />
                  <span style={{ zIndex: 10, position: "relative" }}>
                    {t("barracks:stats.health")}:{" "}
                    <ModifiedStat
                      {...healthPair(
                        props.statModifiers,
                        props?.health,
                        props?.maxHealth,
                        (value) => ThousandsSeparator(roundFloat(value)),
                        (value) => ThousandsSeparator(value)
                      )}
                    />
                  </span>
                </div>
              )}
              {props.hourlyHealing ? (
                <div className="fs-tiny ms-1 mt-2">
                  <span style={{ color: "#44c97a" }}>
                    +{props?.hourlyHealing}/{t("barracks:stats.hourShort")}
                  </span>
                </div>
              ) : (
                ""
              )}
              {!props.hourlyHealing && props.healthBonus ? (
                <>
                  <div className="fs-tiny text-end mt-2 ms-1 ">
                    <span style={{ color: "#44c97a" }}>
                      +{roundFloat(props?.healthBonus)}{" "}
                    </span>
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center">
          {props.level && (
            <button className="level-btn  brk-step4 fw-bold text-light mx-2 ms-4">
              {props.level}
            </button>
          )}
          {props.loading ? (
            <div className="lb-list fs-med px-4 my-0">
              <div className="lb-list-item mb-1">
                <div>
                  <Skeleton width={70} borderRadius={5} height={18} />
                </div>
                <div className="fs-med ms-2">
                  <Skeleton width={40} borderRadius={5} height={18} />
                </div>
              </div>
              <div className="lb-list-item ms-2">
                <div>
                  <Skeleton width={70} borderRadius={5} height={18} />
                </div>
                <div className="fs-med ms-2">
                  {" "}
                  <Skeleton width={40} borderRadius={5} height={18} />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ position: "relative", flex: 1 }}>
              <div
                style={{
                  fontSize: props.train ? 12 : 15,
                  filter:
                    props.state === 3 || props.state === 2 || props.state === 6
                      ? "blur(5px)"
                      : "none",
                }}
                className="lb-list brk-step6 fs-med px-0 my-1"
              >
                <div
                  style={{ flex: props?.abilities ? 0.8 : 1 }}
                  className="lb-list-item  align-items-center "
                >
                  <div>
                    <img
                      alt="damage"
                      style={{ width: 20, height: 23 }}
                      className="me-1"
                      src="/assets/images/icons/damage.png"
                    />
                  </div>
                  <div className="fs-small">{props.damage}</div>
                </div>
                <div
                  style={{ flex: 1 }}
                  className="lb-list-item align-items-center "
                >
                  <div>
                    <img
                      alt="armor"
                      style={{ width: 20, height: 23 }}
                      className="me-1"
                      src="/assets/images/icons/armor.png"
                    />
                  </div>
                  <div className="fs-small">{props.armor}</div>
                </div>
                {props.sellCard && (
                  <>
                    <div
                      style={{ flex: props?.abilities ? 0.8 : 1 }}
                      className="lb-list-item align-items-center "
                    >
                      <div>
                        <img
                          alt="magic"
                          style={{ width: 20, height: 23 }}
                          className="me-1"
                          src="https://core.chainoflegends.com/assets/images/team-magic-resist.png"
                        />
                      </div>
                      <div className="fs-small">{props.magicResistance}</div>
                    </div>
                    <div
                      style={{ flex: 1 }}
                      className="lb-list-item align-items-center "
                    >
                      <div>
                        <img
                          alt="range"
                          style={{ width: 20, height: 23 }}
                          className="me-1"
                          src="/assets/images/icons/range.png"
                        />
                      </div>
                      <div className="fs-small">{props.range}</div>
                    </div>
                  </>
                )}
              </div>
              {!props.sellCard && (
                <div
                  style={{
                    filter:
                      props.state === 3 ||
                      props.state === 2 ||
                      props.state === 6
                        ? "blur(5px)"
                        : "none",
                  }}
                  className="d-flex w-100"
                >
                  <div
                    style={{ flex: props?.abilities ? 0.8 : 1 }}
                    className="lb-list-item align-items-center mb-1"
                  >
                    <div>
                      <img
                        alt="magic"
                        style={{ width: 20, height: 23 }}
                        className="me-1"
                        src="https://core.chainoflegends.com/assets/images/team-magic-resist.png"
                      />
                    </div>
                    <div className="fs-small">{props.magicResistance}</div>
                  </div>
                  <div
                    style={{ flex: 1 }}
                    className="lb-list-item align-items-center mb-1"
                  >
                    <div>
                      <img
                        alt="range"
                        style={{ width: 20, height: 23 }}
                        className="me-1"
                        src="/assets/images/icons/range.png"
                      />
                    </div>
                    <div className="fs-small">{props.range}</div>
                  </div>
                </div>
              )}
              {props?.state === 6 && (
                <div className="fs-med overlay text-start px-3 px-md-2 px-lg-1 mb-2">
                  <h5 className="">{t("barracks:states.forSale")}</h5>
                  <h6 className="bold dark-yellow">
                    {t("barracks:stats.price")}:{" "}
                    {ThousandsSeparator(props.marketOrderPrice)}{" "}
                    <img
                      alt="cleg"
                      src="/assets/images/icons/cleg.png"
                      width={22}
                      height={22}
                    />{" "}
                    {t("barracks:stats.cleg")}
                  </h6>
                </div>
              )}
              {(props.state === 3 || props.state === 2) && (
                <div style={{ top: 0, left: -15 }} className="fs-med overlay  ">
                  <div>
                    <Countdown
                      date={new Date(props.finishUpgradeTime as string)}
                      onComplete={() =>
                        setTimeout(() => {
                          props.refresh && props.refresh();
                        }, 6000)
                      }
                      renderer={({ formatted }) => (
                        <div
                          style={{ transform: "scale(0.8)" }}
                          className="fs-big justify-content-start d-flex"
                        >
                          {parseInt(formatted.days) ? (
                            <div className="time-badge">
                              {formatted.days}
                              <span className="dark-yellow">
                                {t("barracks:timeUnits.days")}
                              </span>
                            </div>
                          ) : (
                            ""
                          )}
                          <div className="ms-2 time-badge">
                            {formatted.hours}
                            <span className="dark-yellow">
                              {t("barracks:timeUnits.hours")}
                            </span>
                          </div>
                          <div className="ms-2 time-badge">
                            {formatted.minutes}
                            <span className="dark-yellow">
                              {t("barracks:timeUnits.minutes")}
                            </span>
                          </div>
                          <div className="ms-2 time-badge">
                            {formatted.seconds}
                            <span className="dark-yellow">
                              {t("barracks:timeUnits.seconds")}
                            </span>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            bottom: "unset",
            top: alteredTheme ? 26 : 10,
            alignItems: "end",
            flexDirection: "column",
            right: 5,
            left: "unset",
            width: "fit-content",
            gap: 1,
          }}
          className="abilities"
        >
          {props.state !== 3 &&
            abilities &&
            abilities.length > 0 &&
            abilities.map((ability, index) => (
              <OverlayTrigger
                key={index}
                placement="bottom"
                flip
                overlay={
                  <Tooltip className="fs-tiny" {...(props as any)}>
                    <CalculateTroopAbility
                      isMarket={props?.isMarket}
                      bonus={props?.bonus}
                      ability={ability}
                      isPercentage={
                        props?.troop?.abilities?.[index]?.ValueType ===
                        "Percentage"
                      }
                    />
                  </Tooltip>
                }
                delay={{ show: 100, hide: 200 }}
              >
                <div
                  style={{
                    marginTop:
                      index !== 0 && (props.hourlyHealing || props.healthBonus)
                        ? 18
                        : 2,
                  }}
                >
                  {ability?.Icon && (
                    <img alt="" src={ability?.Icon} width={35} />
                  )}
                </div>
              </OverlayTrigger>
            ))}
        </div>
      </div>
      {/* Action button section for tiny view */}
      <div
        className="text-center px-2 align-items-center d-flex justify-content-center"
        style={{ marginTop: -22 }}
      >
        {props.loading ? (
          <div className="mt-1">
            <Skeleton width={"90%"} borderRadius={10} height={35} />
          </div>
        ) : (
          props.action
        )}
      </div>
    </div>
  );
};

export default TinyView;
